import { action, computed, makeObservable, observable, runInAction } from 'mobx'

import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { Organization } from '../models/Organization'
import {
  addInstructorToOrganization,
  addOrganizationalAdminToOrganization,
  updateOrganizationState,
  getOrganization,
  removeInstructorFromOrganization,
  removeOrganizationalAdminFromOrganization,
  updateOrganization,
  getOrganizationAdmins,
  getOrganizationInstructors,
  updateHideDefaultProducts,
} from '../firestore/Organization'
import { type AppUser } from '../stores/AppUser'
import { fetchAllAppUsersByRole } from '../firestore/PublicUser'
import type {
  OrganizationInstructorDetails,
  OrganizationInvoiceStatus,
  StaticModelCollection,
} from '../types'
import {
  type OrganizationState,
  SectionRequestState,
  SectionRequestType,
  UserProfileRole,
} from '../types'
import { Catalog } from '../models/Catalog'
import {
  getCatalogs,
  getCatalogsByIds,
  getCatalogsForUser,
} from '../firestore/Catalog'
import { OrganizationCatalog } from '../models/OrganizationCatalog'
import { getOrganizationCatalogs } from '../firestore/OrganizationCatalog'
import { createOrgInvitation } from '../firestore/Invitation'
import { updateOrgInstructor } from '../firestore/OrganizationInstructor'
import type { FirestoreOrganizationInstructor } from '../firestore/OrganizationInstructor/schema'
import {
  addCatalogToUserProfile,
  removeCatalogFromUserProfile,
} from '../firestore/UserProfile'
import { getSectionsByOrganizationId } from '../firestore/Section'
import type { Section } from '../models/Section'
import { getSectionAssignments } from '../firestore/SectionAssignment'
import type { SectionAssignment } from '../models/SectionAssignment'
import { SectionRequest } from '../models/SectionRequest'
import {
  createSectionInvoiceRequest,
  getAllSectionRequestsByOrganizationId,
  updateSectionRequestState,
} from '../firestore/SectionRequest'
import type { PublicUser } from '../models/PublicUser'
import {
  createOrUpdateOrganizationIntegration,
  getOrganizationIntegrations,
} from '../firestore/OrganizationIntegration'
import { OrganizationIntegration } from '../models/OrganizationIntegration'
import { serverTimestamp } from 'firebase/firestore'
import type { UserSearch } from '@breakoutlearning/firebase-repository/stores/UserSearch'
import { fetchUserSearch } from '../firestore/UserSearch'
import {
  createOrganizationCustomProduct,
  updateOrganizationCustomProduct,
  getOrganizationCustomProducts,
  removeOrganizationCustomProduct,
} from '../firestore/OrganizationCustomProduct'
import { OrganizationCustomProduct } from '../models/OrganizationCustomProduct'
import { Institution } from '../models/Institution'
import { getInstitution } from '../firestore/Institution'
import { SlideDeck } from '../models/SlideDeck'
import { getSlideDeck } from '../firestore/SlideDeck'
import { getSettingsSectionPassPricing } from '../firestore/SettingsSectionPassPricing'
import { SettingsSectionPassPricing } from '../models/SettingsSectionPassPricing'
import { SettingsPricing } from '../models/SettingsPricing'
import { getSettingsPricing } from '../firestore/SettingsPricing'
import { SectionProductController } from './SectionProductController'
import { getOrganizationCouponFoldersWithAvailableAndExpiredCounts } from '../firestore/OrganizationSectionPassCouponFolder'
import {
  createFolderWithCoupons,
  fetchOrganizationSectionPassCoupons,
} from '../firestore/OrganizationSectionPassCoupon'
import {
  updateFolderDetails,
  deleteFolderWithCoupons,
} from '../firestore/OrganizationSectionPassCouponFolder'

export class OrganizationCubit extends Cubit {
  repository: FirebaseRepository
  organizationId: string
  organization: Organization
  institution: Institution

  catalogs: StaticModelCollection<Catalog>
  organizationCatalogs: StaticModelCollection<OrganizationCatalog>
  organizationIntegrations: StaticModelCollection<OrganizationIntegration>
  customProducts: StaticModelCollection<OrganizationCustomProduct>
  sectionPassPricing: SettingsSectionPassPricing
  tokenPricing: SettingsPricing

  sectionProductsController: SectionProductController

  private _adminsForOrganization = observable.array<PublicUser>()
  private _instructorsForOrganization =
    observable.array<OrganizationInstructorDetails>()

  private _sectionsForOrganization = observable.array<Section>()
  private _assignmentsForOrganizationBySectionId = observable.map<
    string,
    SectionAssignment[]
  >()

  public slideDecksById = observable.map<string, SlideDeck>({}, { deep: false })

  sectionRequestsForOrganization: StaticModelCollection<SectionRequest>

  @observable
  private _couponFoldersWithCounts = observable.array<{
    id: string
    name: string
    updatedAt: Date | undefined
    expiresAt: Date | undefined
    organizationId: string
    activeCount: number
    consumedCount: number
  }>()

  @observable
  filterSectionRequestsBy: 'pending' | 'not_completed' = 'pending'

  @observable
  private didLoadAdminsForOrganization = false

  @observable
  private didLoadInstructorsForOrganization = false

  @observable
  private didFetchAllInstructors = false

  @observable
  private didLoadAllInstructors = false

  private allInstructors = observable.array<AppUser>()

  userSearchResults = observable.array<UserSearch>()

  @observable
  userSearchFilter: string | undefined

  @observable
  userSearchLoading = false

  constructor(repository: FirebaseRepository, organizationId: string) {
    super()
    makeObservable(this)
    this.repository = repository
    this.organization = Organization.empty(repository)
    this.organizationId = organizationId
    this.catalogs = Catalog.emptyCollection(repository)
    this.organizationCatalogs = OrganizationCatalog.emptyCollection(repository)
    this.organizationIntegrations =
      OrganizationIntegration.emptyCollection(repository)
    this.customProducts = OrganizationCustomProduct.emptyCollection(repository)
    this.sectionRequestsForOrganization =
      SectionRequest.emptyCollection(repository)
    this.institution = Institution.empty(repository)
    this.sectionPassPricing = SettingsSectionPassPricing.empty(repository)
    this.tokenPricing = SettingsPricing.empty(repository)
    this.sectionProductsController = new SectionProductController(repository)
  }

  initialize(): void {
    const isCorre = this.repository.breakoutUser?.isCorre || false
    if (isCorre) {
      // load all catalogs for admin users
      this.addStream(getCatalogs(this.repository), (catalogs) => {
        runInAction(() => {
          this.catalogs.replaceModels(
            catalogs.sort((a, b) =>
              a.data.catalogName.localeCompare(b.data.catalogName)
            )
          )
        })
      })
    }

    this.addStream(
      getOrganization(this.repository, { organizationId: this.organizationId }),
      (org) => {
        this.organization.replaceModel(org)

        if (isCorre) {
          const institutionId = org.data.institutionId
          this.addStream(
            getInstitution(this.repository, { institutionId }),
            (institution) => {
              this.institution.replaceModel(institution)
            }
          )
        }
      }
    )
    this.addStream(
      getOrganizationCatalogs(this.repository, {
        organizationId: this.organizationId,
      }),
      (orgCatalogs) => {
        runInAction(() => {
          this.organizationCatalogs.replaceModels(orgCatalogs)
          // shallow ids copy is made and sorted to ensure that the order of the catalogIds is for sake of key checking
          const catalogIds = orgCatalogs.map(({ id }) => id).sort()
          const streamNamespace = 'catalogs'
          const streamName = `catalogs-${catalogIds.join('-')}`
          if (!isCorre && !this.hasStream(streamName)) {
            // cancel streams in namespace (if any)
            this.removeStreams(streamNamespace)
            ;(this.addStream(
              getCatalogsByIds(this.repository, { catalogIds }),
              (catalogs) => {
                runInAction(() => {
                  this.catalogs.replaceModels(catalogs)
                })
              }
            ),
              {
                name: streamName,
                namespace: streamNamespace,
              })
          }
        })
      }
    )

    this.addStream(
      getOrganizationIntegrations(this.repository, this.organizationId),
      (integrations) => {
        runInAction(() => {
          this.organizationIntegrations.replaceModels(integrations)
        })
      }
    )

    this.addStream(
      getOrganizationCustomProducts(this.repository, {
        organizationId: this.organizationId,
      }),
      (customProducts) => {
        runInAction(() => {
          this.customProducts.replaceModels(customProducts)
        })
      }
    )

    this.addStream(
      getOrganizationAdmins(this.repository, {
        organizationId: this.organizationId,
      }),
      (admins) => {
        runInAction(() => {
          this.didLoadAdminsForOrganization = true
          const adminsSortedAlphabetically = admins.sort((a, b) =>
            a.fullName.localeCompare(b.fullName)
          )
          this._adminsForOrganization.replace(adminsSortedAlphabetically)
        })
      }
    )

    this.addStream(
      getOrganizationInstructors(this.repository, {
        organizationId: this.organizationId,
      }),
      (instructors) => {
        runInAction(() => {
          this.didLoadInstructorsForOrganization = true
          this._instructorsForOrganization.replace(instructors)
        })
      }
    )

    this.addSectionRequestStream()

    this.addStream(
      getSectionsByOrganizationId(this.repository, this.organizationId),
      (sections) => {
        this._sectionsForOrganization.replace(sections)

        this._sectionsForOrganization.forEach((section) => {
          this.addStream(
            getSectionAssignments(this.repository, {
              sectionId: section.id,
            }),
            (assignments) => {
              runInAction(() => {
                this._assignmentsForOrganizationBySectionId.set(
                  section.id,
                  assignments
                )
                this.loadSlideDecksForAssignments(assignments)
              })
            }
          )
        })
      }
    )

    this.addStream(
      getSettingsSectionPassPricing(this.repository),
      (sectionPassPricing) => {
        runInAction(() => {
          this.sectionPassPricing.replaceModel(sectionPassPricing)
        })
      }
    )
    this.addStream(getSettingsPricing(this.repository), (pricing) => {
      runInAction(() => {
        this.tokenPricing.replaceModel(pricing)
      })
    })

    this.addStream(
      getOrganizationCouponFoldersWithAvailableAndExpiredCounts(
        this.repository.firestore,
        this.organizationId
      ),
      (foldersWithCounts) => {
        runInAction(() => {
          this._couponFoldersWithCounts.replace(foldersWithCounts)
        })
      }
    )
  }

  dispose(): symbol {
    this.sectionProductsController.dispose()
    return super.dispose()
  }

  @action
  private loadSlideDecksForAssignments(assignments: SectionAssignment[]) {
    for (const assignment of assignments) {
      const assignmentSection = this.sectionsForOrganization.find(
        (s) => s.id === assignment.data.sectionId
      )

      // don't know pricing info
      if (!assignmentSection) continue

      // uses section pass price so we do not need slide deck price
      if (
        assignmentSection.usesSectionPass &&
        assignmentSection.usesStripePurchase
      ) {
        continue
      }

      const slideDeckId = assignment.data.slideDeckId

      if (this.slideDecksById.has(slideDeckId)) {
        continue
      }

      const slideDeck = SlideDeck.empty(this.repository)
      slideDeck.id = slideDeckId
      this.slideDecksById.set(slideDeckId, slideDeck)

      this.addStream(
        getSlideDeck(this.repository, { slideDeckId }),
        (model) => {
          slideDeck.replaceModel(model)
        }
      )
    }
  }

  addMyselfAsInstructor = async () => {
    if (!this.repository.currentUser) return
    await addInstructorToOrganization(this.repository, {
      userId: this.repository.currentUser?.uid || '',
      organizationId: this.organizationId,
    })
  }

  @action
  setFilterSectionRequestsBy(filter: 'pending' | 'not_completed') {
    this.filterSectionRequestsBy = filter
  }

  @action
  updateSectionRequestsForOrganization = (requests: SectionRequest[]) => {
    this.sectionRequestsForOrganization.replaceModels(requests)
  }

  @computed
  get filteredSectionRequests() {
    if (this.filterSectionRequestsBy === 'pending') {
      return this.sectionRequestsForOrganization.models.filter(
        (request) => request.data.sectionRequestState === 0
      )
    }
    return this.sectionRequestsForOrganization.models.filter(
      (request) => request.data.sectionRequestState !== 0
    )
  }

  addSectionRequestStream() {
    this.addStream(
      getAllSectionRequestsByOrganizationId(
        this.repository,
        this.organizationId
      ),
      this.updateSectionRequestsForOrganization,
      {
        name: 'sectionRequests',
      }
    )
  }

  updateOrgInstructor = async (
    userId: string,
    payload: Pick<FirestoreOrganizationInstructor, 'autoApproveInvoice'>
  ): Promise<void> => {
    await updateOrgInstructor(
      this.repository,
      this.organizationId,
      userId,
      payload
    )
  }

  updateOrganization = async (params: {
    organizationName: string
    organizationCatalogIds: string[]
    organizationDefaultCatalogIds: string[]
    organizationInvoiceStatus: OrganizationInvoiceStatus
  }): Promise<void> => {
    await updateOrganization(this.repository, this.organizationId, {
      ...params,
      currentOrganizationCatalogIds: this.organizationCatalogs.models.map(
        ({ id }) => id
      ),
      currentOrganizationDefaultCatalogIds:
        this.organizationCatalogs.models.reduce<string[]>(
          (acc, { id, data: { defaultCatalogId } }) => {
            if (defaultCatalogId) {
              acc.push(id)
            }
            return acc
          },
          []
        ),
    })
  }

  getCatalogsForInstructor = (userId: string) => {
    return getCatalogsForUser(this.repository, {
      userId,
    })
  }

  updateOrganizationState = async (state: OrganizationState): Promise<void> => {
    await updateOrganizationState(this.repository, this.organizationId, state)
  }

  @computed
  get addableInstructors(): { loading: boolean; users: AppUser[] } {
    if (!this.didFetchAllInstructors) this.fetchInstructors()
    return {
      loading: !this.didLoadAllInstructors,
      // Filter out instructors that are already associated with the organization
      users: this.allInstructors.filter((instructor) => {
        return !this._instructorsForOrganization.find(
          (u) => u.publicUser.id === instructor.uid
        )
      }),
    }
  }

  @computed
  get adminsForOrganization() {
    return {
      loading: !this.didLoadAdminsForOrganization,
      users: this._adminsForOrganization,
    }
  }

  @computed
  get instructorsForOrganization() {
    return {
      loading: !this.didLoadInstructorsForOrganization,
      users: this._instructorsForOrganization,
    }
  }

  get instructorsForOrganizationByInstructorId() {
    const instructors = new Map<string, OrganizationInstructorDetails>()
    this._instructorsForOrganization.forEach((instructor) => {
      instructors.set(instructor.publicUser.id, instructor)
    })
    return instructors
  }

  get sectionsForOrganization() {
    return this._sectionsForOrganization
  }

  get assignmentsForOrganizationBySectionId() {
    return this._assignmentsForOrganizationBySectionId
  }

  // Make it a map
  get sectionsForOrganizationBySectionId() {
    const sections = new Map<string, Section>()
    this._sectionsForOrganization.forEach((section) => {
      sections.set(section.id, section)
    })
    return sections
  }

  get invoiceRequestsForOrganization() {
    return this.sectionRequestsForOrganization.models.filter(
      (request) =>
        request.data.sectionRequestType === SectionRequestType.invoice
    )
  }

  get couponFoldersWithCounts() {
    return this._couponFoldersWithCounts
  }

  @computed
  get organizationCatalogIds() {
    const knownCatalogIds = this.catalogs.models.map(({ id }) => id)
    return this.organizationCatalogs.models
      .map(({ id }) => id)
      .filter((id) => knownCatalogIds.includes(id))
  }

  @computed
  get organizationDefaultCatalogIds() {
    const ids: string[] = []
    this.organizationCatalogs.models.forEach((catalog) => {
      if (catalog.data.defaultCatalogId) {
        ids.push(catalog.id)
      }
    })
    return ids
  }

  @computed
  get sectionsForOrganizationWithAssignmentsAndPrice() {
    return this._sectionsForOrganization.map((section) => {
      const assignments = this._assignmentsForOrganizationBySectionId.get(
        section.id
      )

      const priceInCents = (() => {
        // if uses section pass, defer to section pass price on section or default pricing
        if (section.usesSectionPass) {
          if (section.usesStripePurchase) {
            return (
              section.data.sectionPassPrice ??
              this.sectionPassPricing.data.sectionPassPrice
            )
          }
          if (section.usesCouponCodes) {
            return 'coupon' as const
          }
        }

        if (!assignments) return 'loading' as const

        const slideDecks = assignments.map((assignment) => {
          return this.slideDecksById.get(assignment.data.slideDeckId)
        })

        // if any slide decks are not loaded, return loading
        if (slideDecks.some((sd) => !sd || sd.isEmpty)) {
          return 'loading' as const
        }

        const totalPrice = slideDecks.reduce<number>((acc, slideDeck) => {
          if (slideDeck && slideDeck.data.slideDeckPrice) {
            acc +=
              slideDeck.data.slideDeckPrice * this.tokenPricing.data.tokenPrice
          }
          return acc
        }, 0)

        return totalPrice
      })()

      return {
        section,
        assignments: assignments ?? ('loading' as const),
        priceInCents,
      }
    })
  }

  approveRequest = async (sectionRequest: SectionRequest) => {
    return updateSectionRequestState(
      this.repository,
      sectionRequest.data.sectionId,
      sectionRequest.id,
      this.repository.currentUser?.uid || '',
      SectionRequestState.approved,
      'approved'
    )
  }

  rejectRequest = async (
    sectionRequest: SectionRequest,
    sectionRequestReason: string
  ) => {
    return updateSectionRequestState(
      this.repository,
      sectionRequest.data.sectionId,
      sectionRequest.id,
      this.repository.currentUser?.uid || '',
      SectionRequestState.rejected,
      sectionRequestReason
    )
  }

  @action
  fetchInstructors() {
    this.didFetchAllInstructors = true
    fetchAllAppUsersByRole(this.repository, UserProfileRole.instructor).then(
      (instructors) => {
        runInAction(() => {
          this.didLoadAllInstructors = true
          this.allInstructors.replace(instructors)
        })
      }
    )
  }

  @action
  createOrgInvitation = async (
    args: Omit<Parameters<typeof createOrgInvitation>[1], 'organizationId'>
  ): Promise<string> => {
    return createOrgInvitation(this.repository, {
      ...args,
      organizationId: this.organizationId,
    })
  }

  addInstructor = async (userId: string): Promise<void> => {
    addInstructorToOrganization(this.repository, {
      userId,
      organizationId: this.organizationId,
    })
  }

  addOrganizationalAdmin = async (userId: string): Promise<void> => {
    addOrganizationalAdminToOrganization(this.repository, {
      userId,
      organizationId: this.organizationId,
    })
  }

  removeInstructor = async (instructor: AppUser | string): Promise<void> => {
    removeInstructorFromOrganization(this.repository, {
      userId: typeof instructor === 'string' ? instructor : instructor.uid,
      organizationId: this.organizationId,
    })
  }

  removeOrganizationalAdmin = async (admin: PublicUser): Promise<void> => {
    removeOrganizationalAdminFromOrganization(this.repository, {
      userId: admin.id,
      organizationId: this.organizationId,
    })
  }

  updateCatalogsForUser = async ({
    userId,
    catalogsToRemove,
    catalogsToAdd,
  }: {
    userId: string
    catalogsToRemove: string[]
    catalogsToAdd: string[]
  }) => {
    const addRequests = catalogsToAdd.map((catalogId) =>
      addCatalogToUserProfile(this.repository, {
        catalogId,
        userId,
        organizationId: this.organizationId,
      })
    )
    const removeRequests = catalogsToRemove.map((catalogId) =>
      removeCatalogFromUserProfile(this.repository, { catalogId, userId })
    )

    return await Promise.all([...addRequests, ...removeRequests])
  }

  createOrUpdateOrganizationIntegration = async (
    params: Omit<
      Parameters<typeof createOrUpdateOrganizationIntegration>[1],
      'organizationId'
    >
  ) => {
    return createOrUpdateOrganizationIntegration(this.repository, {
      ...params,
      organizationId: this.organizationId,
    })
  }

  updateHideDefaultProducts = async (hideDefaultProducts: boolean) => {
    await updateHideDefaultProducts(
      this.repository,
      this.organizationId,
      hideDefaultProducts
    )
  }

  createCustomProduct = async (params: {
    productName: string
    sectionPassQuantity: number
    sectionPassPrice: number
    expiresAt?: Date
    productType: string
  }) => {
    return createOrganizationCustomProduct(this.repository, {
      ...params,
      organizationId: this.organizationId,
    })
  }

  updateCustomProduct = async (params: {
    productId: string
    productName?: string
    sectionPassQuantity?: number
    sectionPassPrice?: number
    expiresAt?: Date
  }) => {
    return updateOrganizationCustomProduct(this.repository, {
      ...params,
      organizationId: this.organizationId,
    })
  }

  removeCustomProduct = async (params: { productId: string }) => {
    return removeOrganizationCustomProduct(this.repository, {
      ...params,
      organizationId: this.organizationId,
    })
  }

  createCouponFolder = async (params: {
    folderName: string
    couponCount: number
    expiresAt?: Date | null
  }) => {
    return createFolderWithCoupons({
      firestore: this.repository.firestore,
      organizationId: this.organizationId,
      folderName: params.folderName,
      couponCount: params.couponCount,
      expiresAt: params.expiresAt || null,
    })
  }

  updateCouponFolder = async (params: {
    folderId: string
    folderName?: string
    expiresAt?: Date | null
  }) => {
    return updateFolderDetails({
      firestore: this.repository.firestore,
      organizationId: this.organizationId,
      folderId: params.folderId,
      folderName: params.folderName,
      expiresAt: params.expiresAt,
    })
  }

  deleteCouponFolder = async (folderId: string) => {
    return deleteFolderWithCoupons({
      firestore: this.repository.firestore,
      organizationId: this.organizationId,
      folderId,
    })
  }

  fetchCouponsInFolder = async (params: {
    folderId: string
    consumed: boolean
  }) => {
    return fetchOrganizationSectionPassCoupons({
      repository: this.repository,
      organizationId: this.organizationId,
      folderId: params.folderId,
      consumed: params.consumed,
    })
  }

  /**
   * if section has pending request, approve it.
   * Otherwise, create a new invoice request and then approve it
   */
  markSectionAsInvoiced = async (sectionId: string) => {
    const section = this.sectionsForOrganizationBySectionId.get(sectionId)

    if (!section) throw new Error('Section not found')

    const assignmentCount =
      this.assignmentsForOrganizationBySectionId.get(sectionId)?.length ?? 1

    let pendingRequestId = this.sectionRequestsForOrganization.models.find(
      (r) =>
        r.data.sectionId === sectionId &&
        r.data.sectionRequestState === SectionRequestState.pending
    )?.id

    // if no pending request exists, create a new one
    if (!pendingRequestId) {
      pendingRequestId = (
        await createSectionInvoiceRequest(this.repository, sectionId, {
          organizationId: this.organizationId,
          requestedAt: serverTimestamp(),
          sectionId,
          sectionRequestAssignmentCount: assignmentCount,
          sectionRequestStudentCount: section.data.expectedStudents ?? 1,
          sectionRequestState: SectionRequestState.pending,
          sectionRequestType: SectionRequestType.invoice,
          updatedAt: serverTimestamp(),
          userId: this.repository.uid,
        })
      ).id
    }

    // approve the pending request
    return updateSectionRequestState(
      this.repository,
      sectionId,
      pendingRequestId,
      this.repository.uid,
      SectionRequestState.approved,
      '' //empty reason
    )
  }

  @action
  clearUserSearch = () => {
    this.userSearchFilter = undefined
    this.userSearchResults.clear()
    this.userSearchLoading = false
  }

  searchUsers = async (filter: string): Promise<void> => {
    if (!filter || filter.length < 3) {
      this.clearUserSearch()
      return
    }

    runInAction(() => {
      this.userSearchFilter = filter
      this.userSearchResults.clear()
    })

    if (this.userSearchIsTooBroad) return

    runInAction(() => (this.userSearchLoading = true))

    try {
      const users = await fetchUserSearch(this.repository, 'instructor', filter)

      const usersInOrgLookup = new Set(
        this.instructorsForOrganization.users.map((user) => user.publicUser.id)
      )

      const usersNotInOrg = users.filter(
        (user) => !usersInOrgLookup.has(user.id)
      )

      runInAction(() => this.userSearchResults.replace(usersNotInOrg))
    } finally {
      runInAction(() => (this.userSearchLoading = false))
    }
  }

  @computed
  get userSearchIsTooBroad(): boolean {
    return (
      this.userSearchFilter === 'gmail.com' || this.userSearchFilter === 'gmail'
    )
  }
}
